import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Sparkles, Brain, Calendar, CheckCircle2, ArrowRight, Menu, X,
  Bot, BarChart3, ShoppingCart, TrendingUp, Share2, FileText, Workflow,
  MessageSquare, Lock, Globe, Cpu, Cloud, Rocket, Play, Briefcase, Smartphone,
  Check, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { billingApi, type Plan } from '../api/billing';
import AnimatedBackground from '../components/AnimatedBackground';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [stripeActive, setStripeActive] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Owner directive 2026-10-03: group the subscription packages into tabs so they
  // never wrap across multiple cramped rows. Two groups: everything (Free +
  // All-Modules annual) and the individual per-module subscriptions.
  const [pricingTab, setPricingTab] = useState<'everything' | 'modules'>('everything');

  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    billingApi.getPlans().then(data => {
      setPlans(data.plans);
      setStripeActive(data.stripe_active);
    }).catch(() => {});
    // Verify if the stored token is actually still valid
    if (isAuthenticated) {
      import('../api/http').then(({ default: http }) => {
        http.get('/auth/me').then(() => setTokenValid(true)).catch(() => setTokenValid(false));
      });
    }
  }, [isAuthenticated]);

  const handleGetStarted = () => {
    if (isAuthenticated) navigate('/app');
    else navigate('/register');
  };

  const handleSignIn = () => navigate('/login');

  const modules = [
    { icon: Bot, name: 'Virtual Assistant', desc: 'AI-powered assistant that manages tasks, projects, and your calendar — across web and WhatsApp.', color: 'from-violet-500 to-purple-600', available: true },
    { icon: BarChart3, name: 'Dashboard', desc: 'Unified command center with daily missions, task management, and project tracking.', color: 'from-blue-500 to-cyan-600', available: true },
    { icon: Briefcase, name: 'Job Finder', desc: 'Automated job search, lead tracking, application management, and resume tailoring.', color: 'from-orange-500 to-red-600', available: false },
    { icon: TrendingUp, name: 'Trading', desc: 'Paper and live trading with real-time analytics, risk management, and performance insights.', color: 'from-green-500 to-emerald-600', available: false },
    { icon: Share2, name: 'Social', desc: 'Multi-platform social media management with AI-powered content generation and scheduling.', color: 'from-pink-500 to-rose-600', available: false },
    { icon: FileText, name: 'Content Studio', desc: 'AI-powered content generation for text, images, and video across your projects.', color: 'from-amber-500 to-orange-600', available: false },
    { icon: ShoppingCart, name: 'Marketplace', desc: 'E-commerce management with sales analytics, inventory tracking, and marketplace sync.', color: 'from-indigo-500 to-blue-600', available: false },
    { icon: Workflow, name: 'Automation', desc: 'Build and run automated workflows across your entire workspace.', color: 'from-teal-500 to-cyan-600', available: false },
    { icon: Rocket, name: 'Roadmap', desc: 'Strategic roadmap planning with milestone tracking and progress visualization.', color: 'from-fuchsia-500 to-pink-600', available: false },
  ];

  // Grouped pricing (owner 2026-10-03): the "everything" group is Free + the
  // annual all-modules plan; the "modules" group is the individual subscriptions.
  const everythingPlans = plans.filter(p => p.plan_key === 'free' || p.plan_key === 'all_modules_yearly');
  const modulePlanList = plans.filter(p => p.plan_key.endsWith('_monthly'));
  const groupedPlans = pricingTab === 'everything' ? everythingPlans : modulePlanList;

  // Carousel for the pricing packages (owner 2026-10-03): scroll inside a bounded
  // container (never bleed past the section), snap per card, arrows to page, and
  // neighbour cards peeking at the edges so it's obvious there's more.
  const carouselRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateCarousel = () => {
    const el = carouselRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    setActiveIdx(Math.round(el.scrollLeft / Math.max(1, cardStep())));
  };
  const scrollCarousel = (dir: 1 | -1) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  };
  const [activeIdx, setActiveIdx] = useState(0);
  const cardStep = () => {
    const el = carouselRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    return first ? first.getBoundingClientRect().width + 20 : 310;
  };
  const scrollToCard = (i: number) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: i * cardStep(), behavior: 'smooth' });
  };
  useEffect(() => {
    const id = requestAnimationFrame(updateCarousel);
    const el = carouselRef.current;
    el?.addEventListener('scroll', updateCarousel, { passive: true });
    window.addEventListener('resize', updateCarousel);
    return () => { cancelAnimationFrame(id); el?.removeEventListener('scroll', updateCarousel); window.removeEventListener('resize', updateCarousel); };
  }, []);
  useEffect(() => { requestAnimationFrame(updateCarousel); }, [pricingTab, plans.length]);

  // Short feature highlights per package (richer card detail — owner 2026-10-03).
  const PLAN_FEATURES: Record<string, string[]> = {
    free: ['Dashboard + command center', 'AI virtual assistant', 'Tasks, projects & calendar', 'Bring your own AI provider'],
    dashboard_monthly: ['Full dashboard + command center', 'AI assistant (web + WhatsApp)', 'Tasks, projects & calendar', 'Managed AI routing + storage'],
    jobbot_monthly: ['Automated job search', 'One-click apply + cover letters', 'Resume tailoring', 'Application tracking + proof'],
    trading_monthly: ['Paper & live trading', 'Real-time analytics', 'Risk management', 'Performance insights'],
    social_monthly: ['Multi-platform scheduling', 'AI content generation', 'Per-channel analytics', 'Approval workflow'],
    content_monthly: ['AI text, image & video', 'Brand-voice profiles', 'Reusable asset library', 'Publish to channels'],
    marketplace_monthly: ['Storefront + listings', 'Sales analytics', 'Inventory sync', 'Order management'],
    automation_monthly: ['Visual workflow builder', 'Triggers & schedules', 'Cross-workspace actions', 'Approval gates'],
    roadmap_monthly: ['Milestones & phases', 'Progress tracking', 'Priority planning', 'Stakeholder views'],
    all_modules_yearly: ['Every module unlocked', 'Managed AI routing + storage', 'Email + WhatsApp assistant', 'Best value — save over 60%'],
  };
  const DEFAULT_FEATURES = ['Managed AI routing', 'Cloud storage included', 'Email + WhatsApp assistant', 'Cancel anytime'];

  // Per-package color accent (owner 2026-10-03: "add more color to the cards").
  const PLAN_ACCENT: Record<string, string> = {
    free: 'from-slate-400 to-slate-500',
    dashboard_monthly: 'from-blue-500 to-cyan-500',
    jobbot_monthly: 'from-orange-500 to-red-500',
    trading_monthly: 'from-emerald-500 to-green-500',
    social_monthly: 'from-pink-500 to-rose-500',
    content_monthly: 'from-amber-500 to-orange-500',
    marketplace_monthly: 'from-indigo-500 to-blue-500',
    automation_monthly: 'from-teal-500 to-cyan-500',
    roadmap_monthly: 'from-fuchsia-500 to-pink-500',
    all_modules_yearly: 'from-brand-500 to-purple-600',
  };

  const SAIKLogo = ({ size = 32 }: { size?: number }) => (
    <div className="flex items-center gap-2.5">
      <div
        className="flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-purple-600 shadow-lg shadow-brand-500/30"
        style={{ width: size, height: size }}
      >
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="nav-logo-grad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
              <stop stopColor="#c7d2fe" />
              <stop offset="1" stopColor="#e9d5ff" />
            </linearGradient>
          </defs>
          <path d="M16 40 L20 24 L26 32 L32 20 L38 32 L44 24 L48 40 Z" fill="url(#nav-logo-grad)" opacity="0.98" />
          <path d="M16 40 L48 40 L46 44 L18 44 Z" fill="white" opacity="0.85" />
          <circle cx="32" cy="36" r="3" fill="url(#nav-logo-grad)" />
        </svg>
      </div>
      <span className="text-lg font-bold tracking-tight text-white">Super AI Kingdom</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-ocean text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-ocean/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <SAIKLogo size={36} />
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="#modules" className="text-sm text-slate-400 hover:text-white transition-colors">Modules</a>
              <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors">Pricing</a>
              <a href="#faq" className="text-sm text-slate-400 hover:text-white transition-colors">FAQ</a>
              {isAuthenticated && tokenValid ? (
                <button onClick={() => navigate('/app')} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-950 hover:bg-slate-200 transition-colors">
                  Dashboard
                </button>
              ) : isAuthenticated ? (
                <button onClick={() => navigate('/login')} className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-950 hover:bg-slate-200 transition-colors">
                  Sign In
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <button onClick={handleSignIn} className="text-sm text-slate-400 hover:text-white transition-colors">Sign In</button>
                  <button onClick={handleGetStarted} className="rounded-lg bg-gradient-to-r from-brand-500 to-purple-600 px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity">
                    Get Started Free
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-slate-400">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-ocean px-4 py-4 space-y-3">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white">Features</a>
            <a href="#modules" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white">Modules</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white">Pricing</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white">FAQ</a>
            {isAuthenticated && tokenValid ? (
              <button onClick={() => navigate('/app')} className="flex-1 rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-950">Dashboard</button>
            ) : (
              <>
                <button onClick={handleSignIn} className="flex-1 rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300">Sign In</button>
                <button onClick={handleGetStarted} className="flex-1 rounded-lg bg-gradient-to-r from-brand-500 to-purple-600 px-4 py-2 text-sm font-medium">Get Started</button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Hero with animated background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <AnimatedBackground />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b22_1px,transparent_1px),linear-gradient(to_bottom,#1e293b22_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ocean to-transparent" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-slate-300 backdrop-blur-sm">
            <Sparkles size={14} className="text-brand-400" />
            <span>The last AI tool you'll ever need</span>
          </div>

          <h1 className="mx-auto max-w-4xl text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
            <span className="bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
              The only dashboard
            </span>
            <br />
            <span className="bg-gradient-to-r from-brand-400 via-purple-400 to-violet-400 bg-clip-text text-transparent">
              you'll ever need
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400 sm:text-xl">
            Super AI Kingdom unifies your tasks, projects, virtual assistant, and automation into one
            powerful dashboard. Chat with your AI assistant on web or WhatsApp. Bring your own AI provider
            and stay in control.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={handleGetStarted}
              className="group relative inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-500 to-purple-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:shadow-brand-500/40"
            >
              Start for Free
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="/downloads/saik.apk"
              download
              className="group inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-8 py-3.5 text-base font-medium text-slate-200 hover:bg-white/10 transition-colors backdrop-blur-sm"
              data-agent-id="landing.get-android"
            >
              <Smartphone size={18} className="text-green-400" />
              Get the Android app
            </a>
            <a
              href="#features"
              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-8 py-3.5 text-base font-medium text-slate-200 hover:bg-white/10 transition-colors backdrop-blur-sm"
            >
              <Play size={16} />
              See Features
            </a>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-400" /> Free tier forever</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-400" /> No credit card required</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-green-400" /> Bring your own AI</span>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/20 p-1">
            <div className="h-2 w-1 animate-bounce rounded-full bg-white/40" />
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-white/5 bg-white/[0.02] py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 text-center sm:grid-cols-4">
            <div>
              <div className="text-3xl font-bold text-white">9</div>
              <div className="mt-1 text-sm text-slate-400">Integrated Modules</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">∞</div>
              <div className="mt-1 text-sm text-slate-400">AI Providers Supported</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">24/7</div>
              <div className="mt-1 text-sm text-slate-400">WhatsApp Assistant</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">$8.99</div>
              <div className="mt-1 text-sm text-slate-400">Starting at / month</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Everything you need,{' '}
              <span className="bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
                nothing you don't
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-400">
              One unified platform that replaces your fragmented toolchain with a single, AI-native dashboard.
            </p>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Brain, title: 'AI-Native Assistant', desc: 'Your virtual assistant understands your tasks, projects, and calendar. It can create, update, and manage everything for you — just ask.' },
              { icon: MessageSquare, title: 'WhatsApp Integration', desc: 'Chat with your assistant directly on WhatsApp. Each user gets their own dedicated agent connection — fully private and secure.' },
              { icon: Calendar, title: 'Task & Project Management', desc: 'Create projects, track tasks, set checkpoints, and manage your calendar — all from one unified dashboard or via your assistant.' },
              { icon: Lock, title: 'You Own Your Data', desc: 'Bring your own AI provider keys. Your data stays in your workspace. No middleman, no data selling, no lock-in.' },
              { icon: Cpu, title: 'Multi-Provider AI', desc: 'Connect OpenAI, Anthropic, Ollama, ZAI, or any compatible provider. Switch between models without leaving the platform.' },
              { icon: Cloud, title: '5GB Free Storage', desc: 'Every free account includes 5GB of managed storage. Need more? Upgrade or connect external storage.' },
            ].map((f) => (
              <div key={f.title} className="group rounded-2xl border border-white/5 bg-white/[0.02] p-6 transition-all hover:border-white/10 hover:bg-white/[0.04]">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500/20 to-purple-600/20 text-brand-400">
                  <f.icon size={24} />
                </div>
                <h3 className="text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="border-y border-white/5 bg-white/[0.02] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Powerful modules,{' '}
              <span className="bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
                your way
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-400">
              Start with the dashboard and virtual assistant. Add modules as you grow — each priced to scale with you.
            </p>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {modules.map((m) => (
              <div
                key={m.name}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all hover:border-cyan-400/30"
              >
                <div className={`absolute right-0 top-0 h-32 w-32 bg-gradient-to-br ${m.color} opacity-5 blur-3xl transition-opacity group-hover:opacity-10`} />
                <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${m.color} text-white shadow-lg`}>
                  <m.icon size={22} />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">{m.name}</h3>
                  {m.available ? (
                    <span className="rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">Available</span>
                  ) : (
                    <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-400">Coming Soon</span>
                  )}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Simple,{' '}
              <span className="bg-gradient-to-r from-brand-400 to-purple-400 bg-clip-text text-transparent">
                transparent pricing
              </span>
            </h2>
            <p className="mt-4 text-lg text-slate-400">
              Start free forever. Subscribe to individual modules or get everything with the annual plan.
            </p>

            {/* Grouped tabs (owner 2026-10-03): keep the packages on tabs so they
                never wrap onto multiple cramped lines. */}
            <div className="mt-8 inline-flex items-center gap-1 rounded-xl border border-white/10 bg-white/5 p-1">
              <button
                type="button"
                onClick={() => setPricingTab('everything')}
                data-agent-id="landing.pricing.tab.everything"
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${pricingTab === 'everything' ? 'bg-white text-slate-950' : 'text-slate-400 hover:text-white'}`}
              >
                Free &amp; All-Modules
              </button>
              <button
                type="button"
                onClick={() => setPricingTab('modules')}
                data-agent-id="landing.pricing.tab.modules"
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${pricingTab === 'modules' ? 'bg-white text-slate-950' : 'text-slate-400 hover:text-white'}`}
              >
                Individual Modules
                <span className="ml-1.5 text-xs text-green-400">{modulePlanList.length}</span>
              </button>
            </div>
          </div>

          {/* Bounded carousel (owner 2026-10-03): a contained, snapping scroller
              with edge peeks + arrows so the packages never bleed past the section. */}
          <div className="relative mx-auto mt-12 max-w-6xl">
            {/* Edge fades: hint that more packages sit off-screen left/right. */}
            <div className={`pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-ocean to-transparent transition-opacity duration-200 ${canLeft ? 'opacity-100' : 'opacity-0'}`} />
            <div className={`pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-ocean to-transparent transition-opacity duration-200 ${canRight ? 'opacity-100' : 'opacity-0'}`} />
            <button
              type="button"
              aria-label="Previous packages"
              onClick={() => scrollCarousel(-1)}
              disabled={!canLeft}
              className={`absolute left-0 top-1/2 z-20 inline-flex -translate-y-1/2 -translate-x-2 items-center justify-center rounded-full border border-white/10 bg-slate-900/90 p-2 text-white shadow-lg backdrop-blur transition-opacity sm:-translate-x-3 sm:p-2.5 ${canLeft ? 'opacity-100 hover:bg-slate-800' : 'pointer-events-none opacity-0'}`}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              aria-label="Next packages"
              onClick={() => scrollCarousel(1)}
              disabled={!canRight}
              className={`absolute right-0 top-1/2 z-20 inline-flex -translate-y-1/2 translate-x-2 items-center justify-center rounded-full border border-white/10 bg-slate-900/90 p-2 text-white shadow-lg backdrop-blur transition-opacity sm:translate-x-3 sm:p-2.5 ${canRight ? 'opacity-100 hover:bg-slate-800' : 'pointer-events-none opacity-0'}`}
            >
              <ChevronRight size={20} />
            </button>
            <div
              ref={carouselRef}
              className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-6 pb-6 pt-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {groupedPlans.map((plan) => {
                const isFree = plan.price_cents === 0;
                const isAllYearly = plan.plan_key === 'all_modules_yearly';
                const isPopular = plan.plan_key === 'dashboard_monthly' || isAllYearly;
                const priceStr = isFree ? 'Free' : `$${(plan.price_cents / 100).toFixed(2)}`;
                const intervalLabel = isFree ? 'forever' : `/${plan.billing_interval === 'yearly' ? 'year' : 'month'}`;
                const feats = PLAN_FEATURES[plan.plan_key] || DEFAULT_FEATURES;
                const accent = PLAN_ACCENT[plan.plan_key] || 'from-blue-500 to-cyan-500';

                return (
                  <div
                    key={plan.plan_key}
                    className={`relative flex w-[290px] shrink-0 snap-center flex-col overflow-hidden rounded-2xl border p-6 shadow-xl shadow-black/20 transition-all hover:-translate-y-1 ${
                      isAllYearly
                        ? 'border-brand-400/60 ring-1 ring-brand-400/30'
                        : isPopular
                        ? 'border-white/25'
                        : 'border-white/10'
                    }`}
                  >
                    {/* colored accent wash + top rule (owner 2026-10-03: more color) */}
                    <div className={`pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accent}`} />
                    <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`} />
                    {isAllYearly && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-500 to-purple-600 px-3 py-1 text-xs font-medium text-white">
                        Best Value
                      </div>
                    )}
                    {isPopular && !isAllYearly && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-950">
                        Popular
                      </div>
                    )}
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${accent} text-white shadow-md`}>
                        <Sparkles size={15} />
                      </span>
                      {plan.display_name}
                    </h3>
                    <p className="mt-1 text-sm text-slate-400 min-h-[40px]">{plan.description}</p>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">{priceStr}</span>
                      {!isFree && <span className="text-sm text-slate-400">{intervalLabel}</span>}
                    </div>
                    <ul className="mt-5 space-y-2 text-sm text-slate-300">
                      {feats.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check size={15} className="mt-0.5 shrink-0 text-green-400" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto pt-6">
                      <button
                        onClick={handleGetStarted}
                        className={`block w-full rounded-xl py-2.5 text-sm font-medium transition-all ${
                          isAllYearly
                            ? 'bg-gradient-to-r from-brand-500 to-purple-600 text-white hover:opacity-90'
                            : isFree
                            ? 'bg-white text-slate-950 hover:bg-slate-200'
                            : 'border border-white/10 bg-white/5 text-white hover:bg-white/10'
                        }`}
                      >
                        {isFree ? 'Get Started' : stripeActive ? 'Subscribe' : 'Coming Soon'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            {groupedPlans.length > 1 && (
              <div className="mt-5 flex items-center justify-center gap-2">
                {groupedPlans.map((p, i) => (
                  <button
                    key={p.plan_key}
                    type="button"
                    aria-label={`Go to package ${i + 1}`}
                    onClick={() => scrollToCard(i)}
                    className={`h-2 rounded-full transition-all ${i === activeIdx ? 'w-6 bg-brand-400' : 'w-2 bg-white/20 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            )}
          </div>

          <p className="mt-8 text-center text-sm text-slate-500">
            Free accounts bring their own AI provider and storage. Paid subscriptions include managed AI routing and storage.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/5 bg-white/[0.02] py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold sm:text-4xl">Frequently Asked Questions</h2>
          <div className="mt-12 space-y-6">
            {[
              { q: 'What does "bring your own AI" mean?', a: 'Free accounts connect their own AI provider keys (OpenAI, Ollama, Anthropic, etc.). You control costs and data. Paid subscriptions can optionally use managed AI routing.' },
              { q: 'Can I use my assistant on WhatsApp?', a: 'Yes. Each user connects their WhatsApp number through Meta\'s official Cloud API. Messages route to your personal AI assistant — fully private, per-user.' },
              { q: 'Do I need a credit card to start?', a: 'No. The free tier is forever. You get the dashboard, task management, and virtual assistant. Bring your own AI provider to power the assistant.' },
              { q: 'How does the annual plan work?', a: 'Pay $189.99/year and get access to every module — dashboard, Job Finder, Trading, Social, Content, Marketplace, Automation, and Roadmap. That\'s over 60% savings vs monthly.' },
              { q: 'Can I cancel anytime?', a: 'Yes. Monthly subscriptions can be canceled from your billing portal at any time. You keep access until the end of your billing period.' },
              { q: 'Is my data secure?', a: 'All credentials are encrypted at rest. WhatsApp connections use Meta\'s official Cloud API with per-user scoping. Your assistant only sees your data — never other users\'.' },
            ].map((item) => (
              <div key={item.q} className="rounded-xl border border-white/5 bg-white/[0.02] p-6">
                <h3 className="text-base font-medium text-white">{item.q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-brand-500/10 via-purple-600/5 to-transparent p-12 text-center">
            <div className="absolute left-1/2 top-0 h-[200px] w-[400px] -translate-x-1/2 rounded-full bg-brand-500/20 blur-[80px]" />
            <h2 className="relative text-3xl font-bold sm:text-4xl">
              Ready to build your kingdom?
            </h2>
            <p className="relative mt-4 text-lg text-slate-400">
              Join Super AI Kingdom today. Free forever — upgrade when you're ready.
            </p>
            <button
              onClick={handleGetStarted}
              className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-500 to-purple-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-brand-500/25 hover:shadow-xl hover:shadow-brand-500/40 transition-all"
            >
              Start for Free
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <SAIKLogo size={32} />
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
              <a href="#features" className="hover:text-white">Features</a>
              <a href="#modules" className="hover:text-white">Modules</a>
              <a href="#pricing" className="hover:text-white">Pricing</a>
              <a href="#faq" className="hover:text-white">FAQ</a>
              <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            </div>
            <p className="text-sm text-slate-500">© 2026 Super AI Kingdom. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}